/**
 * Kommissionsabrechnung (Jun-2026, User-Wunsch).
 * - Listet verkaufte (noch nicht abgerechnete) Kommissionsware je Kommissionsgeber.
 * - "Jetzt senden": erstellt Abrechnung, mailt sie an den Lieferanten (mock ohne Key) + archiviert.
 * - Archiv: zeigt versendete Abrechnungen; "Rechnung abgleichen" gleicht die eingehende
 *   Lieferanten-Rechnung gegen die Kommissions-Verbindlichkeiten ab (ohne Lager-Änderung).
 */
import { useState, useEffect, useCallback } from "react";
import { Boxes, Send, FileCheck2, CheckCircle2, Mail, Calendar, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

export default function ConsignmentSection() {
    const [period, setPeriod] = useState("");
    const [data, setData] = useState({ suppliers: [], periods: [], total_net: 0, total_gross: 0 });
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [reconcile, setReconcile] = useState(null); // statement being reconciled

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, st] = await Promise.all([
                api.get("/dealer/consignment/sales", { params: period ? { period } : {} }),
                api.get("/dealer/consignment/statements"),
            ]);
            setData(s.data || { suppliers: [] });
            setStatements(st.data?.statements || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const sendStatement = async (supplier_id) => {
        setBusy(supplier_id);
        try {
            const r = await api.post("/dealer/consignment/statements/send", { supplier_id, period: period || undefined });
            toast.success(r.data?.email_mocked
                ? "Abrechnung archiviert (E-Mail-Versand gemockt — kein Resend-Key)"
                : "Abrechnung erstellt & per E-Mail versendet");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    const doReconcile = async () => {
        if (!reconcile?.invoice_number?.trim()) { toast.error("Rechnungsnummer angeben"); return; }
        setBusy("reconcile");
        try {
            await api.post("/dealer/consignment/statements/reconcile", {
                statement_id: reconcile.id,
                invoice_number: reconcile.invoice_number.trim(),
                invoice_date: reconcile.invoice_date || undefined,
            });
            toast.success("Rechnung abgeglichen — Kommissionsbestand ausgeglichen (Lager unverändert)");
            setReconcile(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    if (loading) return <div className="text-center py-12 text-gray-500" data-testid="consignment-loading">Lade…</div>;

    return (
        <div data-testid="consignment-section">
            {/* Periodenfilter + Summen */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="text-sm border border-gray-300 rounded-sm px-2 py-1.5"
                        data-testid="consignment-period-select"
                    >
                        <option value="">Alle offenen Verkäufe</option>
                        {(data.periods || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="text-sm text-gray-600" data-testid="consignment-totals">
                    Offen gesamt: <b className="text-[#111827] font-mono-num">{eur(data.total_net)}</b> netto
                    <span className="text-gray-400"> / {eur(data.total_gross)} brutto</span>
                </div>
            </div>

            {/* Verkäufe je Kommissionsgeber */}
            {(data.suppliers || []).length === 0 ? (
                <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-sm" data-testid="consignment-empty">
                    <Boxes className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Keine offenen Kommissions-Verkäufe{period ? " in diesem Zeitraum" : ""}.
                </div>
            ) : (
                <div className="space-y-5">
                    {data.suppliers.map((g) => (
                        <div key={g.supplier_id} className="border-2 border-gray-200 rounded-sm overflow-hidden" data-testid={`consignment-supplier-${g.supplier_id}`}>
                            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <div>
                                    <div className="font-bold text-[#111827]">{g.supplier_name || "Unbekannter Lieferant"}</div>
                                    <div className="text-[11px] text-gray-500">{g.count} verkaufte Position(en) · {eur(g.total_net)} netto</div>
                                </div>
                                <Button
                                    onClick={() => sendStatement(g.supplier_id)}
                                    disabled={busy === g.supplier_id}
                                    className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold text-xs"
                                    data-testid={`consignment-send-${g.supplier_id}`}
                                >
                                    <Send className="w-3.5 h-3.5 mr-1.5" /> {busy === g.supplier_id ? "Sende…" : "Abrechnung senden"}
                                </Button>
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 bg-white">
                                        <th className="text-left px-4 py-2">Datum</th>
                                        <th className="text-left px-4 py-2">Artikel</th>
                                        <th className="text-center px-4 py-2">Menge</th>
                                        <th className="text-right px-4 py-2">Netto</th>
                                        <th className="text-left px-4 py-2">Auftrag</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.items.map((it) => (
                                        <tr key={it.liability_id} className="border-t border-gray-100">
                                            <td className="px-4 py-2 text-gray-600">{it.date}</td>
                                            <td className="px-4 py-2 font-medium">{it.name} <span className="text-gray-400 text-xs">{it.sku}</span></td>
                                            <td className="px-4 py-2 text-center font-mono-num">{it.qty}</td>
                                            <td className="px-4 py-2 text-right font-mono-num">{eur(it.amount_net)}</td>
                                            <td className="px-4 py-2 text-gray-500 text-xs">{it.work_order_number || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}

            {/* Archiv */}
            <div className="mt-10">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3 inline-flex items-center gap-2">
                    <FileCheck2 className="w-4 h-4" /> Archivierte Abrechnungen
                </h3>
                {statements.length === 0 ? (
                    <p className="text-sm text-gray-400" data-testid="consignment-archive-empty">Noch keine Abrechnungen erstellt.</p>
                ) : (
                    <div className="border-2 border-gray-200 rounded-sm overflow-hidden" data-testid="consignment-archive">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                                    <th className="text-left px-4 py-2">Zeitraum</th>
                                    <th className="text-left px-4 py-2">Kommissionsgeber</th>
                                    <th className="text-center px-4 py-2">Positionen</th>
                                    <th className="text-right px-4 py-2">Netto</th>
                                    <th className="text-center px-4 py-2">E-Mail</th>
                                    <th className="text-left px-4 py-2">Abgleich</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statements.map((s) => (
                                    <tr key={s.id} className="border-t border-gray-100" data-testid={`consignment-statement-${s.id}`}>
                                        <td className="px-4 py-2 font-mono-num">{s.period_label || s.period}</td>
                                        <td className="px-4 py-2 font-medium">{s.supplier_name}</td>
                                        <td className="px-4 py-2 text-center">{s.count}</td>
                                        <td className="px-4 py-2 text-right font-mono-num">{eur(s.total_net)}</td>
                                        <td className="px-4 py-2 text-center">
                                            {s.email_sent
                                                ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><Mail className="w-3 h-3" />{s.email_mocked ? "Mock" : "Gesendet"}</span>
                                                : <span className="text-[11px] text-gray-400">keine E-Mail</span>}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-1.5">
                                            <Button
                                                size="sm" variant="outline"
                                                onClick={async () => {
                                                    try {
                                                        const r = await api.get(`/dealer/consignment/statements/${s.id}/pdf`, { responseType: "blob" });
                                                        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                                                        const el = document.createElement("a");
                                                        el.href = url; el.download = `Kommissionsabrechnung-${s.period || s.id}.pdf`; el.click();
                                                        URL.revokeObjectURL(url);
                                                    } catch { toast.error("PDF konnte nicht geladen werden"); }
                                                }}
                                                className="h-7 text-[11px] rounded-sm"
                                                data-testid={`consignment-pdf-btn-${s.id}`}
                                                title="Abrechnungs-PDF herunterladen"
                                            >
                                                <Download className="w-3 h-3" />
                                            </Button>
                                            {s.reconciled ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-bold" data-testid={`consignment-reconciled-${s.id}`}>
                                                    <CheckCircle2 className="w-3.5 h-3.5" /> {s.supplier_invoice_number}
                                                </span>
                                            ) : (
                                                <Button
                                                    size="sm" variant="outline"
                                                    onClick={() => setReconcile({ id: s.id, invoice_number: "", invoice_date: "" })}
                                                    className="h-7 text-[11px] rounded-sm"
                                                    data-testid={`consignment-reconcile-btn-${s.id}`}
                                                >
                                                    Rechnung abgleichen
                                                </Button>
                                            )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Reconcile-Dialog */}
            {reconcile && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="consignment-reconcile-modal">
                    <div className="bg-white rounded-sm border-2 border-gray-200 w-full max-w-md p-5">
                        <h4 className="font-bold text-[#111827] mb-1">Lieferanten-Rechnung erfassen</h4>
                        <p className="text-[11px] text-gray-500 mb-4">
                            Die Rechnung wird gegen die Kommissions-Verbindlichkeiten abgeglichen. Der reale
                            Lagerbestand wird dabei NICHT verändert (Ware bereits beim Verkauf ausgebucht).
                        </p>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Rechnungsnummer</label>
                        <input
                            value={reconcile.invoice_number}
                            onChange={(e) => setReconcile((r) => ({ ...r, invoice_number: e.target.value }))}
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3"
                            placeholder="z. B. RE-2026-0815"
                            data-testid="consignment-reconcile-invoice-number"
                        />
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Rechnungsdatum (optional)</label>
                        <input
                            type="date"
                            value={reconcile.invoice_date}
                            onChange={(e) => setReconcile((r) => ({ ...r, invoice_date: e.target.value }))}
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-4"
                            data-testid="consignment-reconcile-invoice-date"
                        />
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setReconcile(null)} className="rounded-sm" data-testid="consignment-reconcile-cancel">Abbrechen</Button>
                            <Button onClick={doReconcile} disabled={busy === "reconcile"} className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="consignment-reconcile-submit">
                                {busy === "reconcile" ? "Gleiche ab…" : "Abgleichen"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
